import React, { useEffect, useState, useRef } from 'react';
import Lottie from 'lottie-react';
import './ResultsLoading.css';

/**
 * TypewriterText Component
 *
 * Displays text with a typewriter animation effect.
 */
const TypewriterText = ({ text, speed = 30, onComplete }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    setDisplayedText('');
    setIsComplete(false);
    let index = 0;

    const timer = setInterval(() => {
      if (index < text.length) {
        setDisplayedText(text.slice(0, index + 1));
        index++;
      } else {
        clearInterval(timer);
        setIsComplete(true);
        if (onComplete) onComplete();
      }
    }, speed);

    return () => clearInterval(timer);
  }, [text, speed, onComplete]);

  return (
    <span>
      {displayedText}
      {!isComplete && <span className="typewriter-cursor">|</span>}
    </span>
  );
};

/**
 * ChatBubble Component
 *
 * A chat bubble styled like the quiz AI messages with optional typewriter animation.
 */
const ChatBubble = ({ message, animate = true, delay = 0, onAnimationComplete }) => {
  const [isVisible, setIsVisible] = useState(delay === 0);
  const [startAnimation, setStartAnimation] = useState(delay === 0);

  useEffect(() => {
    if (delay > 0) {
      const visibilityTimer = setTimeout(() => {
        setIsVisible(true);
        // Small delay before starting typewriter
        setTimeout(() => setStartAnimation(true), 100);
      }, delay);
      return () => clearTimeout(visibilityTimer);
    }
  }, [delay]);

  if (!isVisible) return null;

  return (
    <div className="loading-chat-bubble fade-in">
      {animate && startAnimation ? (
        <TypewriterText text={message} speed={25} onComplete={onAnimationComplete} />
      ) : animate ? (
        <span className="typewriter-cursor">|</span>
      ) : (
        message
      )}
    </div>
  );
};

/**
 * Loading Messages Configuration
 *
 * Messages shown at different time intervals while waiting for results.
 */
const LOADING_MESSAGES = [
  {
    delay: 0,
    message: "Hold tight! I'm working on your results now..."
  },
  {
    delay: 15000, // 15 seconds
    message: "Taking a little longer than usual - your results are just that unique!"
  },
  {
    delay: 30000, // 30 seconds (15 + 15)
    message: "Okay, this is getting a bit embarrassing on my end..."
  },
  {
    delay: 60000, // 60 seconds (1 minute)
    message: "Something seems to have gone wrong. Don't worry - our support team will be in touch soon!"
  }
];

/**
 * ResultsLoading Component
 *
 * Displayed as a secondary loading state after the chat screen typing indicator.
 * Shows avatar video animation while continuing to listen for quiz results.
 * Includes timed chat bubbles with typewriter animation to keep users informed.
 *
 * Props:
 * - onTimeout: Called after the final timeout expires (60 seconds)
 * - onResultsReady: Called when quiz results are detected in Firestore
 * - sessionId: The session ID to listen for quiz results
 */
const ResultsLoading = ({ onTimeout, onResultsReady, sessionId }) => {
  const [visibleMessages, setVisibleMessages] = useState([0]); // Start with first message
  const messageTimersRef = useRef([]);
  const chatContainerRef = useRef(null);

  // Set up timers for subsequent messages
  useEffect(() => {
    // Clear any existing timers
    messageTimersRef.current.forEach(timer => clearTimeout(timer));
    messageTimersRef.current = [];

    // Set up timers for messages after the first one
    LOADING_MESSAGES.slice(1).forEach((msg, index) => {
      const timer = setTimeout(() => {
        setVisibleMessages(prev => [...prev, index + 1]);
      }, msg.delay);
      messageTimersRef.current.push(timer);
    });

    // Final timeout - call onTimeout after last message
    const finalTimer = setTimeout(() => {
      console.log('⏰ ResultsLoading final timeout reached (60s)');
      if (onTimeout) {
        onTimeout();
      }
    }, 65000); // 60s + 5s buffer for last message to display
    messageTimersRef.current.push(finalTimer);

    return () => {
      messageTimersRef.current.forEach(timer => clearTimeout(timer));
    };
  }, [onTimeout]);

  // Auto-scroll to latest message
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [visibleMessages]);

  return (
    <div className="results-loading-container">
      {/* Jaimee Logo */}
      <img
        src="/assets/images/jaimee-logo-cream.svg"
        alt="Jaimee"
        className="results-loading-logo"
      />

      {/* Avatar Animation - Centered, circular, larger, looping Lottie */}
      <div className="results-loading-avatar">
        <Lottie
          animationData={null}
          path="/assets/lottie/Jaimee_S_look_around_lottie.json"
          loop={true}
          autoplay={true}
          style={{ width: '100%', height: '100%' }}
        />
      </div>

      {/* Chat Bubbles Container */}
      <div className="loading-chat-container" ref={chatContainerRef}>
        {visibleMessages.map((msgIndex) => (
          <ChatBubble
            key={msgIndex}
            message={LOADING_MESSAGES[msgIndex].message}
            animate={true}
            delay={0} // Delay is handled by visibleMessages state
          />
        ))}
      </div>
    </div>
  );
};

export default ResultsLoading;
